/**
 * Reads just enough of an X12 835 to decide whether a file may be uploaded, and to show the
 * operator WHICH remittance they are about to send.
 *
 * `POST /provider/clearinghouse/{id}/era/x12/5010/835` creates an ERA from whatever it is given,
 * so a wrong file costs more than a rejected request: an 837 or a 4010 file posted here is a
 * question for the backend, and the same 835 posted twice may create two ERAs (nothing in the
 * request carries an idempotency key). The summary exists so the operator can recognise the file
 * — payer, amount, trace number — before it goes.
 *
 * Deliberately payment-level only: BPR (payment), TRN (check / EFT trace), N1 (payer and payee).
 * The claim loops (CLP, NM1*QC patient names, SVC) are patient data and nothing here reads them.
 */

export interface Era835Payment {
    /** N1*PR — who paid. */
    payerName?: string;
    /** N1*PE — who was paid. */
    payeeName?: string;
    /** BPR02 — total actual provider payment, as written in the file. */
    paymentAmount?: string;
    /** BPR04 — ACH, CHK, NON, FWT, BOP. */
    paymentMethod?: string;
    /** BPR16 — CCYYMMDD, returned as YYYY-MM-DD. */
    paymentDate?: string;
    /** TRN02 — the check or EFT trace number; the payment's identity at the bank. */
    traceNumber?: string;
}

/** Uploadable exactly when `error` is absent. */
export interface Era835Summary {
    /** Why this file must not be uploaded, in words for the operator. */
    error?: string;
    /** GS08, e.g. 005010X221A1. */
    version?: string;
    payments: Era835Payment[];
}

const invalid = (error: string): Era835Summary => ({ error, payments: [] });

/** BPR04, in the words a biller uses. */
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
    ACH: 'ACH / EFT',
    CHK: 'Check',
    NON: 'Non-payment',
    FWT: 'Wire transfer',
    BOP: 'Financial institution option',
};

/** The ISA segment is fixed-width: the separators are read from fixed positions, never assumed. */
const ISA_LENGTH = 106;
const ELEMENT_SEPARATOR_INDEX = 3;
const SEGMENT_TERMINATOR_INDEX = 105;

/** ISA16, the component (sub-element) separator: `PLB03` is a composite of code and reference. */
const COMPONENT_SEPARATOR_INDEX = 104;

export interface IX12Separators {
    element: string;
    component: string;
    segment: string;
}

/**
 * The separators an interchange declares in its fixed-width ISA header, or undefined when the text is not an
 * interchange. Shared by everything that reads a stored or uploaded 835, so none of them assumes `*`, `:` and `~`.
 */
export const readX12Separators = (content: string): IX12Separators | undefined => {
    const text = content.replace(/^\uFEFF/, '').trimStart();
    if (!text.startsWith('ISA') || text.length < ISA_LENGTH) {
        return undefined;
    }
    return {
        element: text[ELEMENT_SEPARATOR_INDEX],
        component: text[COMPONENT_SEPARATOR_INDEX],
        segment: text[SEGMENT_TERMINATOR_INDEX],
    };
};

/** The interchange's segments, each split into its elements. Empty when the text is not an interchange. */
export const x12Segments = (content: string): string[][] => {
    const separators = readX12Separators(content);
    if (!separators) {
        return [];
    }
    return content
        .replace(/^\uFEFF/, '')
        .trimStart()
        .split(separators.segment)
        .map(segment => segment.trim())
        .filter(Boolean)
        .map(segment => segment.split(separators.element));
};

/** The endpoint path says 5010; the 835's 5010 implementation guide is 005010X221(A1). */
const VERSION_5010 = '005010';

const toIsoDay = (ccyymmdd?: string): string | undefined =>
    ccyymmdd && /^\d{8}$/.test(ccyymmdd)
        ? `${ccyymmdd.slice(0, 4)}-${ccyymmdd.slice(4, 6)}-${ccyymmdd.slice(6, 8)}`
        : undefined;

export function summarize835(content: string): Era835Summary {
    // A byte-order mark or leading blank line is common in exported files and harmless.
    const text = content.replace(/^\uFEFF/, '').trimStart();

    if (!text) {
        return invalid('The file is empty.');
    }
    // file.text() decodes as UTF-8 and turns bytes it cannot decode into U+FFFD. The request body
    // must be a string (the generated client JSON-encodes anything else), so such a file would
    // reach the backend with those characters — a name like "Muñoz" in Latin-1 — silently altered.
    if (text.includes('\uFFFD')) {
        return invalid(
            'The file contains characters that could not be read as text (it is not ASCII or UTF-8). Uploading it would alter them — ask the sender for a UTF-8 or ASCII export.',
        );
    }
    if (!text.startsWith('ISA') || text.length < ISA_LENGTH) {
        return invalid('This is not an X12 file — it does not start with an ISA interchange header.');
    }

    const elementSeparator = text[ELEMENT_SEPARATOR_INDEX];
    const segmentTerminator = text[SEGMENT_TERMINATOR_INDEX];
    // Terminators are usually "~", often followed by a line break; some files use the line break
    // itself. Trimming each segment covers both without guessing which.
    const segments = text
        .split(segmentTerminator)
        .map(segment => segment.trim())
        .filter(Boolean)
        .map(segment => segment.split(elementSeparator));

    const transactionSets = segments.filter(elements => elements[0] === 'ST').map(elements => elements[1]);
    if (!transactionSets.length) {
        return invalid('The file has no transaction sets (no ST segment).');
    }
    const other = transactionSets.find(code => code !== '835');
    if (other) {
        return invalid(
            `The file contains an X12 ${other} transaction, not an 835 remittance. Only 835 files can be uploaded here.`,
        );
    }

    const version = segments.find(elements => elements[0] === 'GS')?.[8] || '';
    if (!version.startsWith(VERSION_5010)) {
        return invalid(
            version
                ? `The file is X12 version ${version}. This upload accepts version 5010 (005010X221A1) only.`
                : 'The file has no GS functional group header, so its X12 version cannot be confirmed.',
        );
    }

    // One payment per ST…SE transaction set. Header segments only; the loop stops reading a set
    // at its first CLP (claim payment), which is where patient data begins.
    const payments: Era835Payment[] = [];
    let current: Era835Payment | undefined;
    let inClaims = false;
    for (const elements of segments) {
        const [id] = elements;
        if (id === 'ST') {
            current = {};
            inClaims = false;
            payments.push(current);
        } else if (!current) {
            continue;
        } else if (id === 'SE') {
            current = undefined;
        } else if (id === 'CLP') {
            inClaims = true;
        } else if (inClaims) {
            continue;
        } else if (id === 'BPR') {
            current.paymentAmount = elements[2] || undefined;
            current.paymentMethod = elements[4] || undefined;
            current.paymentDate = toIsoDay(elements[16]);
        } else if (id === 'TRN' && !current.traceNumber) {
            current.traceNumber = elements[2] || undefined;
        } else if (id === 'N1' && elements[1] === 'PR') {
            current.payerName = elements[2] || undefined;
        } else if (id === 'N1' && elements[1] === 'PE') {
            current.payeeName = elements[2] || undefined;
        }
    }

    return { version, payments };
}
